
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import axios from '../../config/axiosConfig';

const PopupAdmin = () => {
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentPopup, setCurrentPopup] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCurrentPopup();
  }, []);

  const fetchCurrentPopup = async () => {
    try {
      const res = await axios.get('/popup/active');
      const popup = res.data?.data;
      if (popup) {
        setCurrentPopup(popup);
        setIsActive(!!popup.isActive);
        if (popup.imageUrl) {
          setPreview(popup.imageUrl);
        }
      }
    } catch (err) {
      // No active popup
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!image) {
      setError('Please select an image.');
      return;
    }
    setLoading(true);
    setMessage('');
    setError('');
    const formData = new FormData();
    formData.append('image', image);
    formData.append('isActive', isActive);
    try {
      const res = await axios.post('/popup/', formData);
      setMessage('Popup updated successfully!');
      setCurrentPopup(res.data?.data || null);
      setImage(null);
      setPreview(res.data?.data?.imageUrl || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update popup.');
    }
    setLoading(false);
  };

  const handleDeactivate = async () => {
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const res = await axios.patch('/popup/deactivate');
      setMessage('Popup deactivated.');
      setCurrentPopup(res.data?.data || null);
      setIsActive(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to deactivate popup.');
    }
    setLoading(false);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 500, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, color: '#1976d2' }}>
        Popup Modal Management
      </Typography>
      <Card elevation={3}>
        <CardContent>
          {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <form onSubmit={handleSubmit}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
                sx={{ alignSelf: 'flex-start' }}
              >
                {image ? 'Change Image' : 'Upload Image'}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageChange}
                />
              </Button>
              {preview && (
                <Box
                  component="img"
                  src={preview}
                  alt="Popup preview"
                  sx={{ maxWidth: '100%', borderRadius: 1 }}
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Active"
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              >
                Save Popup
              </Button>
            </Box>
          </form>
          <Divider sx={{ my: 2 }} />
          <Button
            variant="outlined"
            color="warning"
            onClick={handleDeactivate}
            disabled={loading || !currentPopup?.isActive}
          >
            Deactivate Current Popup
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PopupAdmin;
